import { z } from 'zod';
import { t } from '@lingui/core/macro';

export const COMMON_VALIDATION_ERRORS = {
  required: () => t`This field is required`,
};

export type AllowFormDefaults<Schema> =
  Schema extends Record<string, unknown>
    ? {
        [K in keyof Schema]: Schema[K] | null;
      }
    : Schema;

/**
 * This utility lets you define form default values with null even for required attributes
 */
export function formDefaultValues<Schema>(values: AllowFormDefaults<Schema>): Schema {
  return values as Schema;
}

function isZodIssue(obj: unknown): obj is z.core.$ZodIssue {
  return typeof obj === 'object' && obj !== null && 'code' in obj;
}

export type ZodIssueMap = {
  invalidType?: (issue: z.core.$ZodIssueInvalidType) => string;
  tooBig?: (issue: z.core.$ZodIssueTooBig) => string;
  tooSmall?: (issue: z.core.$ZodIssueTooSmall) => string;
  invalidFormat?: (issue: z.core.$ZodIssueInvalidStringFormat) => string;
  notMultipleOf?: (issue: z.core.$ZodIssueNotMultipleOf) => string;
  unrecognizedKeys?: (issue: z.core.$ZodIssueUnrecognizedKeys) => string;
  invalidKey?: (issue: z.core.$ZodIssueInvalidKey) => string;
  invalidElement?: (issue: z.core.$ZodIssueInvalidElement) => string;
  invalidValue?: (issue: z.core.$ZodIssueInvalidValue) => string;
  custom?: (issue: z.core.$ZodIssueCustom) => string;
};

function mapZodIssue(issue: z.core.$ZodIssue, zodIssueMap: ZodIssueMap): string | undefined {
  if (issue.code === 'invalid_type') {
    return zodIssueMap.invalidType?.(issue);
  } else if (issue.code === 'too_big') {
    return zodIssueMap.tooBig?.(issue);
  } else if (issue.code === 'too_small') {
    return zodIssueMap.tooSmall?.(issue);
  } else if (issue.code === 'invalid_format') {
    return zodIssueMap.invalidFormat?.(issue);
  } else if (issue.code === 'not_multiple_of') {
    return zodIssueMap.notMultipleOf?.(issue);
  } else if (issue.code === 'unrecognized_keys') {
    return zodIssueMap.unrecognizedKeys?.(issue);
  } else if (issue.code === 'invalid_key') {
    return zodIssueMap.invalidKey?.(issue);
  } else if (issue.code === 'invalid_element') {
    return zodIssueMap.invalidElement?.(issue);
  } else if (issue.code === 'invalid_value') {
    return zodIssueMap.invalidValue?.(issue);
  } else if (issue.code === 'custom') {
    return zodIssueMap.custom?.(issue);
  }
  return undefined;
}

export function createFormErrorMapper(zodIssueMap: ZodIssueMap) {
  return (error: unknown) => {
    if (typeof error === 'string') {
      return error;
    }

    if (isZodIssue(error)) {
      return mapZodIssue(error, zodIssueMap) ?? error.message;
    }

    console.warn('Unhandled validation error:', error);

    return t`Unknown validation error`;
  };
}
